import { createContext, ReactNode, useState } from 'react';
import { api } from '../../../server/api';
import {
  IHomeEquity,
  IHomeEquityListItem,
  IHomeEquityPage,
  IUpdateHomeEquity,
} from '../types';

interface HomeEquityContextProviderProps {
  children: ReactNode;
}

interface HomeEquityContextProps {
  allHomeEquities: IHomeEquityListItem[];
  getAllHomeEquities: (clientId: string, playerId?: string) => Promise<void>;
  getHomeEquityById: (
    homeEquityId: string,
    clientId: string
  ) => Promise<IHomeEquity>;
  getHomeEquityPage: (
    homeEquityId: string,
    playerId: string
  ) => Promise<IHomeEquityPage | null>;
  createHomeEquity: (data: IHomeEquity) => Promise<IHomeEquity>;
  updateHomeEquity: (data: IUpdateHomeEquity) => Promise<IHomeEquity>;
  toggleHomeEquityStatus: (
    homeEquityId: string,
    newStatus: boolean
  ) => Promise<void>;
}

export const homeEquityContext = createContext({} as HomeEquityContextProps);

export function HomeEquityContextProvider(
  props: HomeEquityContextProviderProps
) {
  const [allHomeEquities, setAllHomeEquities] = useState<IHomeEquityListItem[]>(
    []
  );

  async function getAllHomeEquities(
    clientId: string,
    playerId?: string
  ): Promise<void> {
    try {
      const url = playerId
        ? `/home-equity/list/${clientId}?playerId=${playerId}`
        : `/home-equity/list/${clientId}`;
      const response = await api.get(url);
      if (response.data.error) {
        throw new Error();
      }
      setAllHomeEquities(response.data.body);
    } catch (error) {
      console.log('getAllHomeEquities error: ', error);
    }
  }

  async function getHomeEquityById(
    homeEquityId: string,
    clientId: string
  ): Promise<IHomeEquity> {
    try {
      const response = await api.get(
        `/home-equity/find/${homeEquityId}/${clientId}`
      );
      if (response.data.error) {
        throw new Error();
      }
      return response.data.body as IHomeEquity;
    } catch (error) {
      console.log('getHomeEquityById error: ', error);
    }
  }

  async function getHomeEquityPage(
    homeEquityId: string,
    playerId: string
  ): Promise<IHomeEquityPage | null> {
    try {
      const response = await api.get(
        `/home-equity/page?homeEquityId=${homeEquityId}&playerId=${playerId}`
      );
      if (response.data.error) {
        throw new Error();
      }
      return response.data.body as IHomeEquityPage;
    } catch (error) {
      console.log('getHomeEquityPage error: ', error);
      return null;
    }
  }

  async function createHomeEquity(data: IHomeEquity): Promise<IHomeEquity> {
    try {
      const response = await api.post('/home-equity/create', data);
      if (response.data.error) {
        throw new Error();
      }
      return response.data.body as IHomeEquity;
    } catch (error) {
      console.log('createHomeEquity error: ', error);
    }
  }

  async function updateHomeEquity(
    data: IUpdateHomeEquity
  ): Promise<IHomeEquity> {
    try {
      const response = await api.put('/home-equity/update', data);
      if (response.data.error) {
        throw new Error();
      }
      return response.data.body as IHomeEquity;
    } catch (error) {
      console.log('updateHomeEquity error: ', error);
    }
  }

  async function toggleHomeEquityStatus(
    homeEquityId: string,
    newStatus: boolean
  ): Promise<void> {
    try {
      const response = await api.patch(
        `/home-equity/status/${homeEquityId}/${newStatus}`
      );
      if (response.data.error) {
        throw new Error();
      }
    } catch (error) {
      console.log('toggleHomeEquityStatus error: ', error);
    }
  }

  return (
    <homeEquityContext.Provider
      value={{
        allHomeEquities,
        getAllHomeEquities,
        getHomeEquityById,
        getHomeEquityPage,
        createHomeEquity,
        updateHomeEquity,
        toggleHomeEquityStatus,
      }}
    >
      {props.children}
    </homeEquityContext.Provider>
  );
}
